import { cn } from 'utils/styles';
import RichTextHeadline from 'components/shared/RichTextHeadline/RichTextHeadline';
import { RelatedArticleProps } from './types';
import { PropsWithClass } from 'types/sharedComponents';
import Link from 'components/shared/Link/Link';
import Media from 'components/shared/Media/Media';
import { ArticleTag } from 'components/ArticleGrid/ArticleTag';

export type SmallArticleClassNames = {
  wrapper: string;
  image: {
    wrapper: string;
    media: string;
  };
  content: {
    wrapper: string;
    title: string;
  };
};

export interface SmallArticleProps extends RelatedArticleProps, PropsWithClass {
  includeCategoryLinks?: boolean;
}

function SmallArticle({ articleTitle, articleImage, slug, category, includeCategoryLinks }: SmallArticleProps) {
  const internalClassNames: SmallArticleClassNames = {
    // focus states mirror hover states for keyboard accessibility
    wrapper: cn(
      'after:border-b after:absolute after:border-solid after:w-full after:-bottom-4 relative flex gap-4',
      'after:transition-colors has-[.article-title:hover]:after:border-chime-green-dark has-[.article-title:focus-within]:after:border-chime-green-dark in-cw-dark-colorways:has-[.article-title:hover]:after:border-highlight in-cw-dark-colorways:has-[.article-title:focus-within]:after:border-highlight',
    ),
    image: {
      wrapper: cn('flex relative aspect-square w-[110px] h-[110px] items-start'),
      media: cn('rounded-md object-fill'),
    },
    content: {
      wrapper: cn(
        'grid grid-rows-[1fr_auto] gap-2',
        'has-[.article-title:hover]:text-chime-green-dark has-[.article-title:focus-within]:text-chime-green-dark in-cw-dark-colorways:has-[.article-title:hover]:text-highlight in-cw-dark-colorways:has-[.article-title:focus-within]:text-highlight',
      ),
      title: cn('article-title flex-1 font-sans text-body-saans font-medium'),
    },
  };

  return (
    <div data-testid="smallArticle" className={internalClassNames.wrapper}>
      {articleImage && (
        <div className={internalClassNames.image.wrapper}>
          <Media media={articleImage} className={internalClassNames.image.media} sizes="110px" />
        </div>
      )}
      <div className={internalClassNames.content.wrapper}>
        <div className={internalClassNames.content.title}>
          {/* Uses Rich Text Headline to prevent new lines from rendering additional space as with RichTextCopy */}
          <Link
            internalPage={{ slug }}
            className="block h-full transition-colors hover:text-chime-green-dark! focus-visible:text-chime-green-dark! in-cw-dark-colorways:hover:text-highlight! in-cw-dark-colorways:focus-visible:text-highlight!">
            <RichTextHeadline level="h3" headline={articleTitle} />
          </Link>
        </div>

        {category?.label && (
          <ArticleTag
            label={category.label}
            href={includeCategoryLinks ? category.url : undefined}
            className="transition-colors"
          />
        )}
      </div>
    </div>
  );
}

export default SmallArticle;
